/**
 * O QUE FAZER COM CADA SERVIÇO, em três respostas lado a lado.
 *
 * A tela do Financeiro mostrava UM valor por cartão — ora a entrada, ora o
 * serviço, conforme o estado — e os motivos em selos por cima dele. Quem abria
 * não conseguia dizer, de relance, se a entrada já tinha saído, se o serviço já
 * tinha sido faturado, nem o que vencia. Os dois documentos são independentes
 * (um por prenhez tem a entrada correndo enquanto o serviço espera a DG60), e
 * espremê-los num número só era a confusão.
 *
 * Agora cada serviço responde:
 *
 *   entrada   a situação da cobrança da entrada (repasse), ou por que não há
 *   servico   a situação da fatura do serviço, ou o que ela espera
 *   passo     UMA ação — a mais urgente — com o verbo do botão
 *
 * A ordem do passo é a mesma de `grupoDoServico`: cobrar o vencido, depois
 * emitir o que não saiu, depois receber o que corre no prazo. Se as duas
 * discordassem, o serviço estaria em "A cobrar" com um botão de faturar.
 *
 * O estado vai por marca e texto, não por cor (ver `stateMarks`): ⚠️ pede ação
 * agora, 🕒 vence em até uma semana, ✅ resolvido.
 */
import { BILLING_MODELS } from '../../constants';
import { entradaCabeNoModelo, etapaQueFecha, entradaAEmitir } from './entrada';

export const MARCA = { ACAO: '⚠️', LOGO: '🕒', OK: '✅' };

/** Até quantos dias à frente um vencimento conta como "vencendo". */
export const JANELA_VENCENDO = 7;

const dia = (v) => (v ? String(v).slice(0, 10) : null);
const iso = (d) => [
  d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0'),
].join('-');
const diasEntre = (de, ate) =>
  Math.round((new Date(`${ate}T00:00:00`) - new Date(`${de}T00:00:00`)) / 86400000);
const dataCurta = (s) => new Date(`${s}T00:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
const dias = (n) => `${n} dia${n === 1 ? '' : 's'}`;

const ROTULO_DA_ETAPA = { opu: 'OPU', fiv: 'FIV', te: 'TE', dg30: 'DG30', dg60: 'DG60' };

/** Em aberto primeiro pelo vencimento; sem data vai para o fim da fila. */
const porVencimento = (a, b) => {
  const va = dia(a.vencimento); const vb = dia(b.vencimento);
  if (va === vb) return (a.numero || 0) - (b.numero || 0);
  if (!va) return 1;
  if (!vb) return -1;
  return va < vb ? -1 : 1;
};

/**
 * A situação de um documento a partir das parcelas dele: a próxima em aberto,
 * ou "paga" quando não sobra nenhuma.
 */
export const situacaoDasParcelas = (parcelas = [], hoje) => {
  if (!parcelas.length) return null;
  const fatura = parcelas[0].numeroDaFatura || null;
  const abertas = parcelas.filter(p => !p.paga_em).sort(porVencimento);

  if (abertas.length === 0) {
    return {
      marca: MARCA.OK, texto: 'paga', urgencia: 'ok',
      valor: parcelas.reduce((t, p) => t + (Number(p.valor) || 0), 0),
      detalhe: fatura ? `fat. ${fatura}` : null,
    };
  }

  const p = abertas[0];
  const venc = dia(p.vencimento);
  const detalhe = [
    fatura ? `fat. ${fatura}` : null,
    p.total > 1 ? `parcela ${p.numero} de ${p.total}` : null,
  ].filter(Boolean).join(' · ') || null;
  const base = { valor: Number(p.valor) || 0, detalhe, parcela: p, vencimento: venc };

  if (!venc) return { ...base, marca: MARCA.ACAO, texto: 'sem vencimento', urgencia: 'acao' };
  const n = diasEntre(hoje, venc);
  if (n < 0) return { ...base, marca: MARCA.ACAO, texto: `vencida há ${dias(-n)}`, urgencia: 'vencida' };
  if (n === 0) return { ...base, marca: MARCA.LOGO, texto: 'vence hoje', urgencia: 'logo' };
  if (n === 1) return { ...base, marca: MARCA.LOGO, texto: 'vence amanhã', urgencia: 'logo' };
  if (n <= JANELA_VENCENDO) return { ...base, marca: MARCA.LOGO, texto: `vence em ${dias(n)}`, urgencia: 'logo' };
  return { ...base, marca: null, texto: `vence ${dataCurta(venc)}`, urgencia: 'prazo' };
};

const apagado = (texto, detalhe = null) => ({ marca: null, texto, detalhe, urgencia: 'nada', apagado: true });

/**
 * As três respostas de um serviço.
 *
 * `motivos` são os de `criticidadeDoServico`, já calculados pela tela; daqui
 * só se lê `nao_faturado` e `entrada_vencida`, para não haver uma segunda
 * definição de "pode faturar" ou de "a entrada venceu".
 */
export const situacaoDoServico = (service, {
  parcelas = [],
  valor = 0,
  aEmitir = null,
  motivos = [],
  faturadoEm = null,
  hoje = iso(new Date()),
} = {}) => {
  const tem = (tipo) => motivos.find(m => m?.tipo === tipo);

  // ── Entrada ────────────────────────────────────────────────────────────
  const daEntrada = parcelas.filter(p => p.ehEntrada);
  let entrada = situacaoDasParcelas(daEntrada, hoje);
  const vencidaPelaTE = tem('entrada_vencida');
  // O prazo da entrada conta da TE, não da parcela: se a regra dos 21 dias já
  // passou, a coluna diz vencida mesmo que a parcela tenha outra data.
  if (entrada && vencidaPelaTE && entrada.urgencia !== 'vencida' && entrada.urgencia !== 'ok') {
    entrada = { ...entrada, marca: MARCA.ACAO, texto: `vencida há ${dias(vencidaPelaTE.dias)}`, urgencia: 'vencida' };
  }
  if (!entrada) {
    if (aEmitir?.deve) {
      entrada = {
        marca: MARCA.ACAO, texto: 'não emitida', urgencia: 'emitir',
        valor: Number(aEmitir.valor) || 0,
        detalhe: aEmitir.quantidade ? `${aEmitir.quantidade} embriões` : null,
      };
    } else if (!entradaCabeNoModelo(service)) {
      entrada = apagado('não se aplica');
    } else if (aEmitir?.motivo === 'te_em_andamento') {
      entrada = apagado('depois da TE');
    } else if (aEmitir?.motivo === 'sem_taxa') {
      entrada = apagado('abonada');
    } else if (aEmitir?.motivo === 'sem_transferidos') {
      entrada = apagado('sem transferidos');
    } else {
      entrada = apagado('não se aplica');
    }
  }

  // ── Serviço ────────────────────────────────────────────────────────────
  const doServico = parcelas.filter(p => !p.ehEntrada);
  let servico;
  if (service?.billing_model === BILLING_MODELS.SEM_COBRANCA) {
    servico = apagado('sem cobrança', faturadoEm ? `faturado em #${faturadoEm}` : null);
  } else if (service?.billed) {
    servico = situacaoDasParcelas(doServico, hoje)
      || (service.paid
        ? { marca: MARCA.OK, texto: 'paga', urgencia: 'ok', valor }
        : { marca: null, texto: 'faturado', urgencia: 'prazo', valor });
  } else if (tem('nao_faturado')) {
    servico = { marca: MARCA.ACAO, texto: 'não faturado', urgencia: 'emitir', valor };
  } else {
    const fecha = etapaQueFecha(service);
    servico = apagado(fecha ? `aguarda ${ROTULO_DA_ETAPA[fecha] || fecha.toUpperCase()}` : '—');
  }

  // ── Próximo passo ──────────────────────────────────────────────────────
  const receber = (cel, rotulo) => ({ acao: 'receber', parcela: cel.parcela, rotulo });
  let passo = null;
  if (entrada.urgencia === 'vencida' && entrada.parcela) passo = receber(entrada, 'Receber entrada');
  else if (servico.urgencia === 'vencida' && servico.parcela) passo = receber(servico, 'Receber');
  else if (entrada.urgencia === 'emitir') passo = { acao: 'emitirEntrada', rotulo: 'Emitir entrada' };
  else if (servico.urgencia === 'emitir') passo = { acao: 'faturar', rotulo: 'Faturar' };
  else {
    // O que corre no prazo: a parcela que vence primeiro, de qualquer documento.
    const abertas = [entrada, servico].filter(c => c.parcela)
      .sort((a, b) => porVencimento(a.parcela, b.parcela));
    if (abertas.length) {
      const c = abertas[0];
      passo = receber(c, c === entrada ? 'Receber entrada' : 'Receber');
    }
  }

  // Para ordenar dentro do grupo: o que vence primeiro, e quem não tem data
  // (emitir, faturar) pela TE, que é quando o relógio começou.
  const datas = [entrada.vencimento, servico.vencimento].filter(Boolean).sort();
  const ordem = datas[0] || dia(service?.te_date) || '9999-12-31';

  return { entrada, servico, passo, ordem };
};

/**
 * `situacaoDoServico` com os dados como as telas os têm em mãos.
 *
 * Um lugar só para montar os argumentos — o Financeiro e o resumo de gestão
 * perguntam a mesma coisa e têm de ouvir a mesma resposta. Foi a montagem em
 * dois lugares que já fez o Financeiro do serviço contar os mesmos fatos com
 * outro resultado (ver `motivosDoServico`).
 *
 * `entradaEmitidaPorServico` nulo é a leitura das faturas que falhou: aí não se
 * oferece emitir, que é justamente quando o app não enxerga a entrada que
 * talvez já exista.
 */
export const situacaoNoContexto = (service, {
  parcelasPorServico = {}, entradaEmitidaPorServico = {}, laboratory = null,
  valor = 0, motivos = [], services = [], hoje,
} = {}) => situacaoDoServico(service, {
  parcelas: parcelasPorServico[service.id] || [],
  valor,
  aEmitir: entradaEmitidaPorServico === null
    ? { deve: false, motivo: 'leitura' }
    : entradaAEmitir(service, laboratory, Boolean(entradaEmitidaPorServico?.[service.id])),
  motivos,
  faturadoEm: services.find(x => x.id === service.billing_service_id)?.service_code || null,
  ...(hoje ? { hoje } : {}),
});

const PESO = { vencida: 0, acao: 0, emitir: 1, logo: 2, prazo: 3, ok: 4, nada: 5 };
const pesoDa = (sit) => Math.min(PESO[sit.entrada.urgencia] ?? 5, PESO[sit.servico.urgencia] ?? 5);

/**
 * O que cobrar e faturar, UMA linha por serviço, na ordem de urgência:
 * vencido primeiro (o mais velho em cima), depois o que falta emitir, depois o
 * que vence na semana. Só entra quem tem próximo passo.
 *
 * O resumo de gestão listava por MOTIVO: a mesma entrada atrasada virava
 * "Entrada vencida há 9d" e "Parcela vencida há 9d" em duas linhas, e ainda
 * aparecia de novo na tabela de parcelas vencidas.
 */
export const linhasDeCobranca = (services = [], situacaoDe) => services
  .map(service => ({ service, sit: situacaoDe(service) }))
  .filter(({ sit }) => sit.passo)
  .sort((a, b) => pesoDa(a.sit) - pesoDa(b.sit)
    || (a.sit.ordem < b.sit.ordem ? -1 : a.sit.ordem > b.sit.ordem ? 1 : 0));

/** A célula em uma linha de texto, para papel: "⚠️ vencida há 9 dias · R$ 760,00 · fat. 2026-0037". */
export const textoDaCelula = (c) => [
  `${c.marca ? `${c.marca} ` : ''}${c.texto}`,
  c.valor > 0 && !c.apagado
    ? Number(c.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : null,
  c.detalhe,
].filter(Boolean).join(' · ');
